/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getGeneralError } from "../../../util/helperFunctions";
import { strings } from "../../../common/Strings/Strings";
import Field from "../../../components/ReactHookFormFields/General/Field";
import { AutoCompleteOptions } from "../../../models/AutoCompleteOptions";
import AlertBox from "../../../components/AlertBox";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal/Modal";
import CloseButton from "../../../components/CloseButton";
import { ClinicDetailsResponse } from "../../../models/management/ClinicDetailsResponse";
import ClinicManagementApi from "../../../api/ClinicManagementApi";
import { UpdateVatRequest } from "../../../models/clinic/UpdateVatRequest";

interface Props {
  clinic: ClinicDetailsResponse;
  close(): void;
  isOpen: boolean;
}

const ChangeVatModal: React.FC<Props> = (props: Props) => {
  const { clinic, close, isOpen } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<UpdateVatRequest>({
    mode: "onChange",
    defaultValues: clinic,
  });

  useEffect(() => {
    if (isOpen) {
      setError(null);
      reset(clinic);
    }
  }, [isOpen, clinic, reset]);

  const submit = async (request: UpdateVatRequest) => {
    setLoading(true);

    try {
      await ClinicManagementApi.changeVatNumber(clinic.id, request);
      close();
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal handleClose={close} show={isOpen}>
      <Modal.Header title={strings.changeVatNumber} />
      <Modal.Body>
        <form id="changeVatForm" onSubmit={handleSubmit(submit)}>
          <Field
            name="vat"
            label={strings.vat}
            register={register}
            autoComplete={AutoCompleteOptions.off}
            error={errors.vat}
            fieldOptions={{
              required: true,
            }}
          />
        </form>
        <AlertBox className="mt-5" message={error} />
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="modal-main-button"
          disabled={loading || !isValid}
          form="changeVatForm"
          loading={loading}
          type="submit"
          variant="primary"
        >
          {strings.save}
        </Button>
        <CloseButton onClick={close} text={strings.cancel} />
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeVatModal;
